import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { mapFreightFromApi } from "../../util/freight/freight-list-mapper";
import { resolveCarrierPriceVisibility } from "../../util/freight/carrierPriceVisibility";
import { resolveFreightDisplayValue, extractFreteProduct } from "../lances/availableFreight.mapper";

export const CARRIER_APPLICATIONS_PAGE_SIZE = 20;

export const TAB_WAITING = "waiting";
export const TAB_ACCEPTED = "accepted";
export const TAB_FINALIZED = "finalized";
export const TAB_LOST = "lost";

/** Abas que listam fretes atribuídos à transportadora (GET /freight/v2). */
export const TABS_USING_ASSIGNED_FREIGHTS = [TAB_ACCEPTED, TAB_FINALIZED];

const FREIGHT_STATUS_PENDING_CONTRACT_CODE = "01";
const FREIGHT_STATUS_FINALIZED_CODE = "07";
const FREIGHT_STATUS_CANCELED_CODE = "08";

const LOST_REASON_LABELS = {
  ASSIGNED_TO_OTHER: "Atribuído a outra transportadora",
  FREIGHT_CANCELED: "Frete cancelado",
};

function normalizeStatusCode(code) {
  if (code == null || code === "") return "";
  return String(code).padStart(2, "0");
}

export function isPendingContractFreight(freightStatus) {
  const code = normalizeStatusCode(freightStatus?.code);
  if (code === FREIGHT_STATUS_PENDING_CONTRACT_CODE) return true;

  const describe = String(freightStatus?.describe ?? "").toLowerCase();
  return describe.includes("pendente de contrata");
}

export function isFinalizedFreight(freightStatus) {
  const code = normalizeStatusCode(freightStatus?.code);
  if (code === FREIGHT_STATUS_FINALIZED_CODE) return true;

  const describe = String(freightStatus?.describe ?? "").toLowerCase();
  return describe.includes("finalizado");
}

export function isAssignedFreightInProgress(freightStatus) {
  const code = normalizeStatusCode(freightStatus?.code);
  return (
    code !== FREIGHT_STATUS_FINALIZED_CODE &&
    code !== FREIGHT_STATUS_CANCELED_CODE
  );
}

function formatLocation(place, fallback = "—") {
  if (!place) return fallback;
  if (typeof place === "string") return place;
  if (place.label) return place.label;
  const city = place.city;
  const uf = place.uf || place.state;
  if (city && uf) return `${city}/${uf}`;
  return city || uf || fallback;
}

function formatFreightNumber(item) {
  if (item.numberSerial != null && item.numberSerial !== "") {
    return String(item.numberSerial);
  }
  const code = item.freightCode;
  if (!code) return "—";
  return String(code).replace(/^FT-/i, "");
}

function resolveApplicationStatusLabel(item, applicationStatus) {
  if (item.applicationStatusLabel) return item.applicationStatusLabel;

  switch (applicationStatus) {
    case "ACCEPTED":
      return "Transportadora aceita";
    case "FINALIZED":
      return "Frete finalizado";
    case "LOST":
      return (
        LOST_REASON_LABELS[item.lostReason] || "Atribuído a outra transportadora"
      );
    case "CANCELED":
      return LOST_REASON_LABELS.FREIGHT_CANCELED;
    default:
      return "Aguardando análise";
  }
}

export function mapCarrierApplicationItem(item, options = {}) {
  const applicationStatus = String(item.applicationStatus || "").toUpperCase();
  const origin =
    item.originLabel ||
    formatLocation(item.origin) ||
    formatLocation(item.originCity);
  const destination =
    item.destinationLabel ||
    formatLocation(item.destination) ||
    formatLocation(item.destinationCity);
  const freightStatus = item.freightStatus || item.status || null;
  const { freightValue, pricingType } = resolveFreightDisplayValue(item);
  const priceVisibility = resolveCarrierPriceVisibility(item, {
    viewerShipperIds: options.viewerShipperIds,
    totalPrice: freightValue,
    pricingType,
    fallbackShipperName: item.shipperName || "—",
  });

  return {
    freightId: item.freightId,
    number: formatFreightNumber(item),
    freightCode: item.freightCode,
    origin,
    destination,
    collectionDate: item.collectionDate,
    fixedPriceAmount: priceVisibility.displayPrice,
    pricingType,
    shipperName: priceVisibility.displayShipperName,
    applicationStatus,
    applicationStatusLabel: resolveApplicationStatusLabel(item, applicationStatus),
    registeredAt: item.enteredAt || item.registeredAt,
    updatedAt: item.updatedAt,
    freightStatusDescribe: freightStatus?.describe || "",
    freightStatusCode: normalizeStatusCode(freightStatus?.code),
    lostReason: item.lostReason,
    lostReasonLabel: LOST_REASON_LABELS[item.lostReason] || null,
    acceptedCarrierId: item.acceptedCarrierId || null,
    acceptedCarrierName: item.acceptedCarrierName || null,
    canGoToFreightList: item.canGoToFreightList === true,
    isAccepted: applicationStatus === "ACCEPTED",
    isWaiting: applicationStatus === "WAITING",
    isFinalized: applicationStatus === "FINALIZED",
    isLost:
      applicationStatus === "LOST" ||
      applicationStatus === "CANCELED" ||
      applicationStatus === "CANCELLED",
    priceVisibility,
  };
}

function formatFreightRouteCity(record, kind) {
  const place = kind === "origin" ? record?.firstDelivery : record?.lastDelivery;
  if (!place) return "—";
  const city = place.city;
  const uf = place.uf;
  if (city && uf) return `${city}/${uf}`;
  return city || uf || "—";
}

function buildFreightV2PricingSource(rawItem = {}, record = {}) {
  const freight =
    rawItem?.freight && typeof rawItem.freight === "object"
      ? rawItem.freight
      : record?.freight && typeof record.freight === "object"
        ? record.freight
        : {};
  const values =
    rawItem?.values && typeof rawItem.values === "object"
      ? rawItem.values
      : freight?.values && typeof freight.values === "object"
        ? freight.values
        : {};
  const freteProduct = extractFreteProduct({ ...rawItem, freight });

  return {
    ...rawItem,
    freight,
    values,
    newProducts: rawItem?.newProducts ?? freight?.newProducts,
    pricingType:
      rawItem?.pricingType ??
      freight?.pricingType ??
      values?.pricingType ??
      freteProduct?.pricingType,
    pricePerTon:
      rawItem?.pricePerTon ??
      freight?.pricePerTon ??
      values?.pricePerTon ??
      freteProduct?.priceList,
    fixedPriceAmount:
      rawItem?.fixedPriceAmount ??
      freight?.fixedPriceAmount ??
      values?.fixedPriceAmount,
    valueFreightage:
      rawItem?.valueFreightage ??
      freight?.valueFreightage ??
      values?.valueFreightage ??
      rawItem?.freightValue ??
      freteProduct?.total ??
      freteProduct?.netPrice,
  };
}

/** Mapeia frete atribuído à transportadora (GET /freight/v2) para o card de Meus Lances. */
export function mapFreightV2ToApplicationItem(rawItem, tab, options = {}) {
  const record = mapFreightFromApi(rawItem);
  const freightStatus = record.status || {};
  const isFinalizedTab = tab === TAB_FINALIZED;
  const pricingSource = buildFreightV2PricingSource(rawItem, record);
  const { freightValue, pricingType } = resolveFreightDisplayValue(pricingSource);
  const priceVisibility = resolveCarrierPriceVisibility(
    {
      ...pricingSource,
      shipper: record.shipper || pricingSource.shipper,
      shipperUid: record.shipper?.uid || pricingSource.shipperUid,
      shipperName:
        record.clientPayment?.name || record.shipper?.name || "—",
      transport: rawItem.transport || record.transport || pricingSource.transport,
      driver: rawItem.driver || record.driver || pricingSource.driver,
      priceVisibility: rawItem.priceVisibility || pricingSource.priceVisibility,
    },
    {
      viewerShipperIds: options.viewerShipperIds,
      totalPrice: freightValue,
      pricingType,
      fallbackShipperName:
        record.clientPayment?.name || record.shipper?.name || "—",
    },
  );

  return {
    freightId: record.id,
    number: formatFreightNumber(record),
    freightCode: record.numberSerial,
    origin: formatFreightRouteCity(record, "origin"),
    destination: formatFreightRouteCity(record, "destination"),
    collectionDate: record.firstDelivery?.dateColeta,
    fixedPriceAmount: priceVisibility.displayPrice,
    pricingType,
    shipperName: priceVisibility.displayShipperName,
    applicationStatus: isFinalizedTab ? "FINALIZED" : "ACCEPTED",
    applicationStatusLabel: isFinalizedTab ? "Frete finalizado" : "Frete aceito",
    registeredAt: null,
    updatedAt: rawItem.updatedAt || rawItem.updated_at || null,
    freightStatusDescribe: freightStatus.describe || "",
    freightStatusCode: normalizeStatusCode(freightStatus.code),
    lostReason: null,
    lostReasonLabel: null,
    acceptedCarrierId: null,
    acceptedCarrierName: null,
    canGoToFreightList: true,
    isAccepted: !isFinalizedTab,
    isWaiting: false,
    isFinalized: isFinalizedTab,
    isLost: false,
    priceVisibility,
  };
}

export function filterApplicationsForTab(items, tab) {
  if (!Array.isArray(items)) return [];

  switch (tab) {
    case TAB_WAITING:
      return items.filter(
        (item) =>
          item.isWaiting &&
          isPendingContractFreight({
            code: item.freightStatusCode,
            describe: item.freightStatusDescribe,
          }),
      );
    case TAB_LOST:
      return items.filter((item) => item.isLost);
    case TAB_ACCEPTED:
      return items.filter(
        (item) =>
          item.isAccepted &&
          isAssignedFreightInProgress({
            code: item.freightStatusCode,
            describe: item.freightStatusDescribe,
          }),
      );
    case TAB_FINALIZED:
      return items.filter(
        (item) =>
          item.isFinalized ||
          isFinalizedFreight({
            code: item.freightStatusCode,
            describe: item.freightStatusDescribe,
          }),
      );
    default:
      return items;
  }
}

export function filterAssignedFreightsForTab(records, tab) {
  if (!Array.isArray(records)) return [];

  if (tab === TAB_FINALIZED) {
    return records.filter((record) =>
      isFinalizedFreight(record?.status),
    );
  }

  if (tab === TAB_ACCEPTED) {
    return records.filter((record) =>
      isAssignedFreightInProgress(record?.status),
    );
  }

  return records;
}

export function parseFreightV2ApplicationsResponse(response, cursorForPage) {
  const payload = response?.data ?? {};
  const items = Array.isArray(payload.data)
    ? payload.data
    : payload?.data?.items || [];
  const pagination = payload.pagination ?? {};
  const nextCursor = pagination.nextCursor ?? pagination.next_cursor ?? null;
  const hasMore =
    pagination.hasMore === true &&
    typeof nextCursor === "string" &&
    nextCursor.trim().length > 0 &&
    nextCursor !== cursorForPage;

  return {
    items,
    nextCursor,
    hasMore,
    count: pagination.count ?? payload.count ?? items.length,
  };
}

export function parseCarrierApplicationsResponse(response, cursorForPage) {
  const root = response?.data ?? {};
  const items = Array.isArray(root.data)
    ? root.data
    : Array.isArray(root)
      ? root
      : [];
  const pagination = root.pagination ?? {};
  const nextCursor = pagination.nextCursor ?? pagination.next_cursor ?? null;
  const hasMore =
    pagination.hasMore === true &&
    typeof nextCursor === "string" &&
    nextCursor.trim().length > 0 &&
    nextCursor !== cursorForPage;

  return {
    items,
    nextCursor,
    hasMore,
    count: pagination.count ?? root.count ?? items.length,
  };
}

export function formatApplicationDate(value) {
  if (!value) return "—";
  try {
    const date = typeof value === "string" ? parseISO(value) : new Date(value);
    if (!isValid(date)) return "—";
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
}

export function formatWaitingSince(value) {
  if (!value) return null;
  try {
    const date = typeof value === "string" ? parseISO(value) : new Date(value);
    if (!isValid(date)) return null;
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch {
    return null;
  }
}

export function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(num);
}

export function getApiErrorMessage(error, fallback = "Ocorreu um erro inesperado.") {
  return (
    error?.response?.data?.message ??
    error?.response?.data?.msg ??
    error?.response?.data?.error ??
    error?.message ??
    fallback
  );
}
